import { Analytics } from '@cobliteam/analytics';
import Avatar, { AvatarFallback, AvatarImage } from '@cobliteam/colib-ui/Avatar';
import Icon from '@cobliteam/colib-ui/Icon';
import { lazy } from '@cobliteam/lazy-with-retries';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@cobliteam/ui-business/components/AppSidebar/components/Sidebar';
import TooltipOnIntent from '@cobliteam/ui-business/components/TooltipOnIntent/TooltipOnIntent';
import { Suspense, useState } from 'react';

// O menu (Base UI) sai do chunk inicial (ADR-0022): o sidebar monta em toda página.
const AppFleetSwitcherMenu = lazy(() => import('./AppFleetSwitcherMenu'));

interface Fleet {
  name: string;
  id: string;
}

interface FleetSwitcherProps {
  fleets: Fleet[];
  currentFleet: Fleet;
}

const getFleetInitials = (fleetName: string) => {
  return fleetName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
};

/**
 * Renderiza o seletor de frota no sidebar, com pesquisa, lista de frotas ranqueada por acessos e opção de troca.
 *
 * Ao abrir o menu registra um evento de analytics e dá foco no campo de pesquisa; ao selecionar outra frota despacha a ação de mudança de frota,
 * incrementa o ranking de acesso local (localStorage) e registra o evento correspondente.
 *
 * @param fleets - Lista de frotas disponíveis exibidas e filtradas; cada item deve conter pelo menos `id` e `name`.
 * @param currentFleet - Frota atualmente selecionada; deve conter `id` e `name`.
 * @returns O elemento React que representa o componente AppFleetSwitcher.
 */
function AppFleetSwitcher({ fleets = [], currentFleet = { name: '', id: '' } }: FleetSwitcherProps) {
  const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
  const [open, setOpen] = useState(false);
  const { openMobile } = useSidebar();

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) Analytics.track('FleetSwitcher - Open');
    setOpen(isOpen);
  };

  // scale-110 sem overflow-hidden: a imagem arredondada cobre o fundo do Avatar e corta a margem transparente dos PNGs; o outline por dentro cobre o vão da margem.
  const trigger = openMobile ? (
    <SidebarMenuButton data-testid='fleet-switcher' size='lg' className='h-11 cursor-pointer px-2 data-popup-open:bg-sidebar-accent data-popup-open:text-sidebar-accent-foreground'>
      <div className='flex aspect-square items-center justify-center rounded-lg text-sidebar-primary-foreground'>
        <Avatar className='size-8 rounded-md after:hidden after:rounded-md dark:bg-neutral-200'>
          {!isDemoMode && (
            <AvatarImage
              src={`https://assets.cobli.co/fleets/${currentFleet.id}/logo.png`}
              className='scale-110 rounded-md outline-2 outline-border -outline-offset-2 dark:outline-neutral-500 dark:brightness-90'
            />
          )}
          <AvatarFallback className='rounded-md bg-primary font-semibold text-primary-foreground text-xs'>{getFleetInitials(currentFleet.name)}</AvatarFallback>
        </Avatar>
      </div>
      <div className='grid min-w-0 flex-1 text-left text-sm leading-5'>
        <span className='truncate font-semibold'>{currentFleet.name}</span>
      </div>
      <Icon name='chevrons-up-down' size='sm' className='ml-auto text-sidebar-foreground' />
    </SidebarMenuButton>
  ) : (
    <div className='mt-1' data-testid='fleet-switcher'>
      <div className='flex items-center justify-center text-sidebar-primary-foreground'>
        <TooltipOnIntent
          render={
            <Avatar className='!cursor-pointer size-9 rounded-lg shadow-sm after:hidden after:rounded-lg dark:bg-neutral-200'>
              {!isDemoMode && (
                <AvatarImage
                  src={`https://assets.cobli.co/fleets/${currentFleet.id}/logo.png`}
                  className='scale-110 rounded-lg outline-2 outline-border -outline-offset-2 dark:outline-neutral-500 dark:brightness-90'
                />
              )}
              <AvatarFallback className='rounded-lg bg-primary font-semibold text-primary-foreground text-xs'>{getFleetInitials(currentFleet.name)}</AvatarFallback>
            </Avatar>
          }
          triggerProps={{ delay: 400 }}
          contentProps={{ side: 'right' }}
        >
          {currentFleet.name}
        </TooltipOnIntent>
      </div>
    </div>
  );

  // Enquanto o chunk do menu carrega, o clique fica guardado no estado: o menu controlado já abre ao montar.
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Suspense
          fallback={
            <div className='contents' onClickCapture={() => handleOpenChange(true)}>
              {trigger}
            </div>
          }
        >
          <AppFleetSwitcherMenu fleets={fleets} currentFleet={currentFleet} open={open} onOpenChange={handleOpenChange} trigger={trigger} />
        </Suspense>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export default AppFleetSwitcher;
